/* Composer un devis : un client, une usine, des lignes prises au catalogue.
   Le prix part de la fiche produit et se corrige à la main, ligne par ligne.

   Le catalogue s'ouvre en panneau par-dessus l'écran (voir Choisir) : avec
   quatre-vingts références chez une usine, on coche ce qu'on prend et on
   revient au devis d'un coup, au lieu de faire défiler ligne par ligne.

   Une ligne se saisit en CARTONS. On ne vend pas trente-sept pots, on vend
   trois cartons de douze : le carton est l'unité de commande, l'unité n'en
   est que la conséquence. Et comme un même article existe en plusieurs
   formats, le conditionnement se change depuis la ligne elle-même — c'est là
   qu'on relit le devis, donc là qu'on s'aperçoit qu'on a pris le pot au lieu
   du sac. */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useClients, usePrixAgent, usePrixClient, useProduits, useUsines,
} from "@/lib/donnees";
import { autorise, useNiveau, useSession } from "@/lib/session";
import { Empty, Kpi, Kpis, Section } from "@/ui/primitives";
import { Champ } from "@/ui/Drawer";
import { Choisir } from "./Choisir";
import { Icon } from "@/ui/Icon";
import { useToast } from "@/ui/Toast";
import { aujourdhui, entier, euro } from "@/lib/format";
import { haussesDe, planchersDe, tarifsDe } from "@/domain/prix";
import { grouperArticles, libelleFormat, nomArticle } from "@/domain/catalogue";
import { parCarton, phraseCartons, totalLigne, unites } from "@/domain/devis";
import { demanderBaisse, propagerHausses } from "@/features/prix/actions";
import { creerDevis } from "@/features/ventes/actions";
import type { LigneSaisie } from "@/features/ventes/actions";

export function NouveauDevis() {
  const navigue = useNavigate();
  const toast = useToast();
  const { profil } = useSession();
  const voitUsine = autorise(useNiveau(), "agent");

  const { donnees: clients } = useClients();
  const { donnees: usines } = useUsines();
  const { donnees: produits } = useProduits();
  const { donnees: prixAgent } = usePrixAgent();
  const { donnees: prixClient, recharger: rechargerTarifs } = usePrixClient();

  const [clientId, setClientId] = useState("");
  const [usineId, setUsineId] = useState("");
  const [contact, setContact] = useState("");
  const [date, setDate] = useState(aujourdhui());
  const [ouvert, setOuvert] = useState(false);
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const [occupe, setOccupe] = useState(false);

  /* Ce qui est déjà au devis ne doit pas se reproposer. On le calcule ici,
     une fois, plutôt que dans le panneau qui se rouvre. */
  const dejaLa = useMemo(
    () => new Set(lignes.map((l) => l.produit_id).filter((x): x is string => Boolean(x))),
    [lignes],
  );
  const dispos = useMemo(
    () => produits.filter((p) => p.usine_id === usineId && p.actif),
    [produits, usineId],
  );
  const nbRefs = dispos.length;

  /* Les autres formats du même article, par référence : c'est ce qui peuple
     le sélecteur de conditionnement d'une ligne. */
  const freres = useMemo(() => {
    const m = new Map<string, typeof dispos>();
    for (const a of grouperArticles(dispos)) {
      for (const f of a.formats) m.set(f.id, a.formats);
    }
    return m;
  }, [dispos]);

  /* Le plancher de celui qui saisit : la direction, qui n'a pas de tarif
     propre, retombe sur le prix de référence du produit. */
  const planchers = useMemo(
    () => planchersDe(produits, prixAgent, profil?.id ?? ""),
    [produits, prixAgent, profil?.id],
  );
  /* Ce que CE client paie : son tarif s'il en a un, le plancher sinon. C'est
     de là que part chaque ligne, et c'est lui qu'une hausse met à jour. */
  const tarifs = useMemo(
    () => tarifsDe(planchers, prixClient, clientId || null),
    [planchers, prixClient, clientId],
  );

  const sousLePlancher = (l: LigneSaisie) => {
    const mini = l.produit_id ? planchers.get(l.produit_id) : undefined;
    return mini != null && l.prix > 0 && l.prix < mini;
  };
  const bloquantes = lignes.filter(sousLePlancher);
  const basses = bloquantes.length;
  const hausses = haussesDe(lignes, tarifs);

  const total = lignes.reduce((s, l) => s + totalLigne(l.cartons, l.par_carton, l.prix), 0);
  const quantite = lignes.reduce((s, l) => s + l.qte, 0);
  const cartons = lignes.reduce((s, l) => s + Math.max(0, Math.floor(l.cartons || 0)), 0);
  const complet = Boolean(clientId && usineId && lignes.length);

  function changerUsine(id: string) {
    if (id !== usineId && lignes.length) {
      setLignes([]);
      toast("Les lignes ont été vidées : elles appartenaient à l'autre usine.");
    }
    setUsineId(id);
  }

  /* Le prix auquel une référence partirait : ce que ce client paie déjà — son
     tarif, ou à défaut le plancher de l'agent. Jamais le prix de référence
     seul. Le panneau l'affiche, et la ligne en part. */
  const prixDe = (id: string) =>
    tarifs.get(id) ?? Number(produits.find((p) => p.id === id)?.prix ?? 0);

  /** Ajoute d'un coup ce qui a été coché, dans l'ordre du catalogue. */
  function ajouter(ids: readonly string[]) {
    const neuves: LigneSaisie[] = [];
    for (const id of ids) {
      const p = produits.find((x) => x.id === id);
      if (!p) continue;
      const par = parCarton(p.par_carton);
      neuves.push({
        produit_id: p.id,
        ref: p.ref,
        nom: p.nom,
        gamme: p.gamme,
        conditionnement: p.conditionnement,
        prix: prixDe(p.id),
        cartons: 1,
        par_carton: p.par_carton,
        qte: unites(1, par),
      });
    }
    if (!neuves.length) return;
    setLignes((l) => [...l, ...neuves]);
    toast(
      neuves.length > 1
        ? `${entier(neuves.length)} références ajoutées — reste à poser les quantités.`
        : `${neuves[0]?.ref} ajoutée.`,
    );
  }

  /* Un seul endroit écrit `qte`, et il la tire toujours des cartons : deux
     chiffres saisis séparément finiraient par se contredire, et la base le
     refuserait — à juste titre. */
  const majLigne = (i: number, patch: Partial<LigneSaisie>) =>
    setLignes((l) =>
      l.map((x, k) => {
        if (k !== i) return x;
        const n = { ...x, ...patch };
        return { ...n, qte: unites(n.cartons, n.par_carton) };
      }),
    );

  /** Change le format d'une ligne : nouvelle référence, nouveau carton, même
      nombre de cartons. Le prix repart de ce que ce client paie pour CE
      format — garder l'ancien ferait un devis faux dès le premier regard. */
  function changerFormat(i: number, produitId: string) {
    const p = dispos.find((x) => x.id === produitId);
    if (!p) return;
    majLigne(i, {
      produit_id: p.id,
      ref: p.ref,
      nom: p.nom,
      gamme: p.gamme,
      conditionnement: p.conditionnement,
      par_carton: p.par_carton,
      prix: prixDe(p.id),
    });
  }

  const retirer = (i: number) => setLignes((l) => l.filter((_, k) => k !== i));

  async function enregistrer() {
    if (!profil || !complet || occupe) return;
    setOccupe(true);
    try {
      /* Sous le plancher, la ligne part au plancher : le devis n'est pas
         bloqué, c'est le prix qui l'est. La demande suit, et c'est elle qui
         ouvrira la porte — en abaissant le plancher de cet agent, pas celui
         des autres. */
      const demandees = bloquantes.map((l) => ({
        produitId: l.produit_id as string,
        prix: l.prix,
        plancher: planchers.get(l.produit_id as string) as number,
      }));
      const aEnvoyer = lignes.map((l) =>
        sousLePlancher(l)
          ? { ...l, prix: planchers.get(l.produit_id as string) as number }
          : l,
      );

      const v = await creerDevis({
        client_id: clientId,
        usine_id: usineId,
        agent_id: profil.id,
        contact: contact.trim() || null,
        date_document: date,
        lignes: aEnvoyer,
      });

      for (const d of demandees) {
        try {
          await demanderBaisse({
            produitId: d.produitId, clientId: clientId || null, venteId: v.id,
            prix: d.prix, plancher: d.plancher, motif: `devis ${v.reference}`,
          });
        } catch {
          /* Une demande qui ne part pas ne doit pas annuler le devis. */
        }
      }
      /* Ce que le devis apprend au tarif du client. Seules les hausses
         remontent : une remise ponctuelle n'efface pas un tarif acquis. */
      const montees = clientId ? await propagerHausses(clientId, hausses, v.reference) : 0;
      rechargerTarifs();
      const dit = [`Devis ${v.reference} enregistré`];
      if (montees) {
        dit.push(`${entier(montees)} tarif${montees > 1 ? "s" : ""} client relevé${montees > 1 ? "s" : ""}`);
      }
      if (demandees.length) {
        dit.push(
          `${entier(demandees.length)} ligne${demandees.length > 1 ? "s" : ""} au plancher, ` +
          `baisse${demandees.length > 1 ? "s" : ""} en attente de validation`,
        );
      }
      toast(dit.join(" · ") + ".");
      navigue(`/ventes/${v.id}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'enregistrement a échoué.");
      setOccupe(false);
    }
  }

  return (
    <div className="pad">
      <Kpis>
        <Kpi
          k="Lignes"
          v={entier(lignes.length)}
          n={`${entier(cartons)} carton${cartons > 1 ? "s" : ""} · ${entier(quantite)} unités`}
        />
        <Kpi k="Total HT" v={euro(total)} n="montant du devis" />
        {basses ? (
          <Kpi
            k="Sous le prix mini"
            v={<span className="marge-ko">{entier(basses)}</span>}
            n={`${basses > 1 ? "lignes partiront" : "ligne partira"} au plancher, une demande suivra`}
          />
        ) : null}
        {hausses.length ? (
          <Kpi
            k="Tarif client relevé"
            v={entier(hausses.length)}
            n={hausses.length > 1 ? "références au-dessus du tarif actuel" : "référence au-dessus du tarif actuel"}
          />
        ) : null}
        <Kpi
          k="Prix moyen"
          v={euro(quantite ? total / quantite : 0)}
          n="par unité vendue"
        />
      </Kpis>

      <Section titre="En-tête du devis">
        <div className="card card-pad">
          <div className="champ-duo">
            <Champ id="d-client" label="Client">
              <select id="d-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Choisir un client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                    {c.type === "lead" ? " (prospect)" : ""}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ id="d-usine" label={voitUsine ? "Usine" : "Origine des produits"}>
              <select id="d-usine" value={usineId} onChange={(e) => changerUsine(e.target.value)}>
                <option value="">Choisir…</option>
                {usines
                  .filter((u) => u.actif)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {voitUsine ? u.nom : (u.categorie ?? "Origine")}
                    </option>
                  ))}
              </select>
            </Champ>
          </div>
          <div className="champ-duo">
            <Champ id="d-contact" label="Contact chez le client">
              <input
                id="d-contact"
                type="text"
                value={contact}
                placeholder="Nom de la personne"
                onChange={(e) => setContact(e.target.value)}
              />
            </Champ>
            <Champ id="d-date" label="Date du document">
              <input id="d-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Champ>
          </div>
        </div>
      </Section>

      <Section
        titre="Lignes du devis"
        note={lignes.length ? `${lignes.length} références` : "aucune ligne"}
      >
        <div className="card card-pad">
          {lignes.length === 0 ? (
            <Empty
              titre="Devis vide"
              detail={
                usineId
                  ? "Ouvrez le catalogue pour cocher les références à mettre au devis."
                  : "Choisissez une usine : son catalogue s'ouvrira ensuite."
              }
            />
          ) : (
            <ul className="lignes">
              {lignes.map((l, i) => {
                const formats = l.produit_id ? freres.get(l.produit_id) ?? [] : [];
                const par = parCarton(l.par_carton);
                const bas = sousLePlancher(l);
                return (
                  <li className="ligne" key={l.produit_id ?? `${l.ref}-${i}`}>
                    <div className="ligne-t">
                      <span className="ligne-nom">
                        {l.produit_id ? nomArticle(l.nom, l.gamme) : l.nom}
                      </span>
                      <span className="ligne-ref mono">{l.ref}</span>
                      <button
                        type="button"
                        className="icon-btn ligne-x"
                        aria-label={`Retirer ${l.ref}`}
                        onClick={() => retirer(i)}
                      >
                        <Icon n="fermer" cls="" />
                      </button>
                    </div>

                    <div className="ligne-champs">
                      <label className="ch">
                        <span>Conditionnement</span>
                        {formats.length > 1 ? (
                          <select
                            value={l.produit_id ?? ""}
                            aria-label={`Conditionnement pour ${l.ref}`}
                            onChange={(e) => changerFormat(i, e.target.value)}
                          >
                            {formats.map((f) => (
                              <option key={f.id} value={f.id}>
                                {libelleFormat(f).nom}
                                {libelleFormat(f).poids ? ` · ${libelleFormat(f).poids}` : ""}
                                {f.par_carton ? ` — ${f.par_carton}/carton` : ""}
                                {` · ${f.ref}`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="ch-fixe">
                            {l.gamme ?? l.conditionnement ?? "à l'unité"}
                            {par > 1 ? ` — ${par}/carton` : ""}
                          </span>
                        )}
                      </label>

                      <label className="ch ch-n">
                        <span>Cartons</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          aria-label={`Nombre de cartons pour ${l.ref}`}
                          value={l.cartons}
                          onChange={(e) =>
                            majLigne(i, { cartons: Math.max(0, Number(e.target.value) || 0) })
                          }
                        />
                      </label>

                      <label className="ch ch-n">
                        <span>Prix unitaire</span>
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          inputMode="decimal"
                          aria-label={`Prix unitaire pour ${l.ref}`}
                          className={bas ? "sous-plancher" : ""}
                          value={l.prix}
                          onChange={(e) =>
                            majLigne(i, { prix: Math.max(0, Number(e.target.value) || 0) })
                          }
                        />
                      </label>
                    </div>

                    <div className="ligne-f">
                      <span className="ligne-calc">
                        {phraseCartons(l.cartons, l.par_carton)} × {euro(l.prix)}
                      </span>
                      <span className="ligne-total mono">
                        {euro(totalLigne(l.cartons, l.par_carton, l.prix))}
                      </span>
                    </div>

                    {bas ? (
                      <div className="ligne-alerte">
                        Sous le prix mini de {euro(planchers.get(l.produit_id ?? "") ?? 0)} —
                        la ligne partira au plancher, une demande de baisse suivra.
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="fiche-actions">
            <button
              type="button"
              className="btn"
              disabled={!usineId}
              title={usineId ? undefined : "Choisissez d'abord une usine"}
              onClick={() => setOuvert(true)}
            >
              <Icon n="catalogue" cls="" />
              {lignes.length ? "Ajouter des références" : "Ouvrir le catalogue"}
              {usineId ? <span className="btn-n">{entier(nbRefs)}</span> : null}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!complet || occupe}
              title={complet ? undefined : "Choisissez un client, une usine et au moins une ligne"}
              onClick={() => void enregistrer()}
            >
              {occupe ? "Enregistrement…" : `Enregistrer le devis — ${euro(total)}`}
            </button>
            {lignes.length ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={occupe}
                onClick={() => setLignes([])}
              >
                Vider les lignes
              </button>
            ) : null}
          </div>
        </div>
      </Section>

      {ouvert && usineId ? (
        <Choisir
          usineId={usineId}
          nomUsine={
            voitUsine
              ? (usines.find((u) => u.id === usineId)?.nom ?? "usine")
              : (usines.find((u) => u.id === usineId)?.categorie ?? "origine")
          }
          produits={produits}
          dejaLa={dejaLa}
          prixDe={prixDe}
          onAjouter={ajouter}
          onClose={() => setOuvert(false)}
        />
      ) : null}
    </div>
  );
}
